import React from "react";
import { useTranslation } from "react-i18next";
import { MdRestaurantMenu } from "react-icons/md";
import { Skeleton } from "antd";
import { RoomIcon, CorrectIcon } from "src/assets/Icons";
import { formatNumber } from "src/utility";
import { useHotelDynamicDetails } from "../../context";
import {
  getMealTypeLabel,
  getInclusionTags,
  getCancellationInfo,
  getMandatorySupplements,
  formatSupplementDescription,
} from "src/Modules/Hotels/utils/room";
import Amenities from "../../../HotelBooking/components/Amenities/Amenities";

export const RoomSelection: React.FC = () => {
  const { t } = useTranslation();
  const { selectedRoom: room, hotel, rateConditionRoom, isRateConditionsFetching } = useHotelDynamicDetails();

  if (!room) return null;

  const roomTitle = (rateConditionRoom?.name ?? []).filter(Boolean).join(" & ");
  const mealLabel = room.mealType ? getMealTypeLabel(room.mealType) : null;
  const cancellation = getCancellationInfo(rateConditionRoom?.cancelPolicies ?? [], rateConditionRoom?.isRefundable ?? false);
  const inclusionTags = rateConditionRoom ? getInclusionTags(rateConditionRoom) : [];
  const promotions = (rateConditionRoom?.roomPromotion ?? []).filter(Boolean);
  const mandatorySupplements = getMandatorySupplements(rateConditionRoom?.supplements?.flat() ?? []);

  return (
    <div className="border-border-primary flex w-full flex-col gap-4 rounded-xl border-s-4 border-solid bg-background-default py-4 pe-4 ps-5">
      <div className="flex items-center gap-2 justify-between">
        <div className="flex items-center gap-2">
          <RoomIcon className="text-font-default h-5 w-5" />
          <p className="font-inter text-font-default text-base font-bold leading-6">{t("Hotels.booking.roomSelection")}</p>
        </div>
        <div>
          <Amenities amenities={rateConditionRoom?.amenities} isLoading={isRateConditionsFetching} />
        </div>
      </div>

      <div className="border-border-primary/20 flex flex-col items-start gap-1 border-s-2 border-solid ps-4">
        {roomTitle && (
          <div className="flex items-center gap-2">
            <p className="font-inter text-font-default text-sm font-medium leading-6">{roomTitle}</p>
          </div>
        )}

        <div className="flex flex-wrap gap-2 pt-1">
          {mealLabel && (
            <span className="text-font-informative inline-flex w-fit items-center gap-1.5 rounded-sm bg-background-informative px-3 py-1.5 text-xs font-bold">
              <MdRestaurantMenu className="h-4 w-4" />
              {mealLabel}
            </span>
          )}

          {isRateConditionsFetching ? (
            Array.from({ length: 5 }, (_, i) => <Skeleton.Button key={i} active size="small" style={{ width: 90, height: 20 }} />)
          ) : (
            <>
              <span
                className={`inline-flex w-fit items-center gap-1.5 rounded-sm px-3 py-1.5 text-xs font-bold ${
                  cancellation.refundable
                    ? "bg-background-success text-font-success"
                    : "bg-background-danger text-font-danger"
                }`}
              >
                {cancellation.refundable ? t("Flights.listing.refundable") : t("Hotels.common.nonRefundable")}
              </span>

              {cancellation.hasFreeCancellation && (
                <span className="inline-flex w-fit items-center gap-1.5 rounded-sm bg-background-success px-3 py-1.5 text-xs font-bold text-font-success">
                  <CorrectIcon className="h-4 w-4 text-font-success" />
                  {cancellation.freeCancelUntil
                    ? t("Hotels.common.freeCancellationUntil", { date: cancellation.freeCancelUntil })
                    : t("Hotels.common.freeCancellation")}
                </span>
              )}

              {cancellation.paidPolicies.map((policy, i) => (
                <span
                  key={i}
                  className="inline-flex w-fit items-center gap-1.5 rounded-sm bg-background-danger px-3 py-1.5 text-xs font-bold text-font-danger"
                >
                  {t(
                    policy.chargeType === "Percentage"
                      ? "Hotels.common.cancellationChargePercentage"
                      : "Hotels.common.cancellationChargeAmount",
                    {
                      date: policy.fromDate,
                      charge: policy.chargeType === "Percentage" ? policy.cancellationCharge : formatNumber(policy.cancellationCharge),
                      currency: policy.currency ?? hotel?.currency,
                    },
                  )}
                </span>
              ))}

              {promotions.map((promotion, i) => (
                <span
                  key={i}
                  className="text-font-informative inline-flex w-fit items-center gap-1.5 rounded-sm bg-background-informative px-3 py-1.5 text-xs font-bold"
                >
                  <CorrectIcon className="h-4 w-4" />
                  {promotion}
                </span>
              ))}

              {inclusionTags.map((tag, i) => (
                <span
                  key={i}
                  className="text-font-informative inline-flex w-fit items-center gap-1.5 rounded-sm bg-background-informative px-3 py-1.5 text-xs font-bold"
                >
                  <CorrectIcon className="h-4 w-4" />
                  {tag}
                </span>
              ))}

              {mandatorySupplements.length > 0 && (
                <span className="text-font-warning bg-background-warning inline-flex w-fit items-center gap-1.5 rounded-sm px-3 py-1.5 text-xs font-bold">
                  {mandatorySupplements
                    .map(
                      (s) =>
                        `${formatSupplementDescription(s.description)}${s.price > 0 ? ` - ${t("Hotels.common.payAtHotel", { price: formatNumber(s.price), currency: s.currency ?? "" })}` : ""}`,
                    )
                    .join(" · ")}
                </span>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
